<template>
  <SidebarComponent />

  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container pt-orders-export">
        <div class="titolo-card">
          <h1>Ricerca ordini PT</h1>
          <p class="subtitle">
            Tool dedicato per trovare ordini con sedute PT tramite categoria prodotto e ricerca nome, poi esportarli in CSV.
          </p>
        </div>

        <section class="card-surface filters-card">
          <div class="filters-grid">
            <div class="field">
              <label for="pt-from">Dal</label>
              <input id="pt-from" v-model="filters.startDate" type="date" />
            </div>

            <div class="field">
              <label for="pt-to">Al</label>
              <input id="pt-to" v-model="filters.endDate" type="date" />
            </div>

            <div class="field field-wide">
              <label>Intervalli rapidi</label>
              <div class="quick-actions">
                <button type="button" class="btn tiny ghost" @click="applyBeforeNovember2025">
                  <span class="material-icons" aria-hidden="true">history</span>
                  Prima di nov 2025
                </button>
                <button type="button" class="btn tiny ghost" @click="applyFromNovember2025">
                  <span class="material-icons" aria-hidden="true">today</span>
                  Da nov 2025
                </button>
                <button type="button" class="btn tiny ghost" @click="clearDates">
                  <span class="material-icons" aria-hidden="true">event_busy</span>
                  Tutte le date
                </button>
              </div>
            </div>

            <div class="field">
              <label for="pt-match-mode">Tipo ricerca</label>
              <select id="pt-match-mode" v-model="filters.matchMode">
                <option value="smart">Categoria PT oppure nome</option>
                <option value="category">Solo categoria</option>
                <option value="name">Solo nome prodotto</option>
              </select>
            </div>

            <div class="field">
              <label for="pt-category">Categoria</label>
              <select id="pt-category" v-model="filters.category">
                <option value="__pt__">PT automatico</option>
                <option v-for="category in categoryOptions" :key="category" :value="category">
                  {{ category }}
                </option>
              </select>
            </div>

            <div class="field">
              <label for="pt-status">Stato</label>
              <select id="pt-status" v-model="filters.status">
                <option value="">Tutti</option>
                <option v-for="status in statusOptions" :key="status" :value="status">
                  {{ formatLabel(status) }}
                </option>
              </select>
            </div>

            <div class="field">
              <label for="pt-source">Sorgente</label>
              <select id="pt-source" v-model="filters.source">
                <option value="">Tutte</option>
                <option value="woocommerce">WooCommerce</option>
                <option value="legacy">Legacy</option>
                <option value="manuale">Manuale</option>
              </select>
            </div>

            <div class="field">
              <label for="pt-studio">Studio</label>
              <select id="pt-studio" v-model="filters.studio">
                <option value="">Tutti</option>
                <option v-for="studio in studioOptions" :key="studio" :value="studio">
                  {{ studio }}
                </option>
              </select>
            </div>

            <div class="field">
              <label for="pt-customer">Cliente</label>
              <input
                id="pt-customer"
                v-model.trim="filters.customer"
                type="search"
                placeholder="Nome, email, telefono, ordine"
              />
            </div>

            <div class="field">
              <label for="pt-max-docs">Limite ordini</label>
              <input id="pt-max-docs" v-model.number="filters.maxDocs" type="number" min="100" step="100" />
            </div>

            <div class="field field-full">
              <label for="pt-product-terms">Ricerca nome prodotto</label>
              <textarea
                id="pt-product-terms"
                v-model="filters.productTerms"
                rows="3"
                placeholder="Una keyword per riga o separata da virgole"
              ></textarea>
              <small>
                Esempi gia inclusi: 50 pt, 50 sedute pt, 10 pt, 10 sedute pt, 20 sedute pt online.
              </small>
            </div>
          </div>

          <div class="toolbar-actions">
            <button type="button" class="btn primary staff-button-neutral" :disabled="loading" @click="searchOrders">
              <span class="material-icons" aria-hidden="true">search</span>
              {{ loading ? 'Ricerca...' : 'Cerca ordini PT' }}
            </button>
            <button type="button" class="btn ghost" :disabled="loading || !ptRows.length" @click="exportCsv">
              <span class="material-icons" aria-hidden="true">download</span>
              Esporta CSV
            </button>
            <button type="button" class="btn ghost" :disabled="loading || !ptRows.length" @click="exportGroupedCsv">
              <span class="material-icons" aria-hidden="true">table_view</span>
              Esporta raggruppato
            </button>
            <button type="button" class="btn ghost" :disabled="loading" @click="resetFilters">
              <span class="material-icons" aria-hidden="true">restart_alt</span>
              Reset
            </button>
          </div>

          <p v-if="lastUpdatedAt" class="filters-note">
            Ultimo aggiornamento: {{ formatDateTime(lastUpdatedAt) }}
            <span v-if="orders.length"> - Ordini caricati: {{ formatNumber(orders.length) }}</span>
            <span v-if="limitedByMaxDocs"> - Risultato limitato a {{ formatNumber(filters.maxDocs) }} documenti</span>
          </p>
        </section>

        <section v-if="error" class="card-surface error-card">
          {{ error }}
        </section>

        <section v-if="hasSearched && !loading" class="metrics-grid">
          <div class="metric-box">
            <span class="metric-label">Ordini PT</span>
            <span class="metric-value">{{ formatNumber(uniqueOrderCount) }}</span>
          </div>
          <div class="metric-box">
            <span class="metric-label">Righe prodotto PT</span>
            <span class="metric-value">{{ formatNumber(ptRows.length) }}</span>
          </div>
          <div class="metric-box">
            <span class="metric-label">Sedute stimate</span>
            <span class="metric-value">{{ formatNumber(totalPtSessions) }}</span>
          </div>
          <div class="metric-box">
            <span class="metric-label">Sedute fatte abbinate</span>
            <span class="metric-value">{{ formatNumber(totalPtDoneSessions) }}</span>
          </div>
          <div class="metric-box">
            <span class="metric-label">Totale righe PT</span>
            <span class="metric-value">{{ formatCurrency(totalPtAmount) }}</span>
          </div>
        </section>

        <section v-if="loading" class="card-surface">
          <p class="muted">Caricamento ordini...</p>
        </section>

        <section v-else-if="ptRows.length" class="card-surface results-card">
          <div class="table-wrap">
            <table class="results-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th>Stato</th>
                  <th>Prodotto</th>
                  <th>Categoria</th>
                  <th>PT</th>
                  <th>Fatte</th>
                  <th>Totale riga</th>
                  <th>Match</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in ptRows" :key="row.rowKey">
                  <td>{{ row.orderDateLabel }}</td>
                  <td>
                    <strong>{{ row.customerName || 'Senza nome' }}</strong>
                    <small>{{ row.customerEmail || row.orderId || '-' }}</small>
                  </td>
                  <td>
                    <span class="status-chip">{{ formatLabel(row.status) }}</span>
                  </td>
                  <td>
                    <strong>{{ row.productName }}</strong>
                    <small>{{ row.productSku || row.productId || '-' }}</small>
                  </td>
                  <td>{{ row.categoryLabel }}</td>
                  <td>
                    <strong>{{ formatNumber(row.ptSessions) }}</strong>
                    <small>{{ row.sessionMode }}</small>
                  </td>
                  <td>
                    <strong>{{ formatOptionalNumber(row.ptDoneSessions) }}</strong>
                    <small>{{ ptDoneLabel(row) }}</small>
                  </td>
                  <td>{{ formatCurrency(row.productTotal) }}</td>
                  <td>
                    <span class="match-pill">{{ row.matchReason }}</span>
                  </td>
                  <td>{{ row.source }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-else-if="hasSearched" class="card-surface empty-state">
          Nessun ordine PT trovato con i filtri correnti.
        </section>

        <section v-else class="card-surface empty-state">
          Imposta i filtri e premi “Cerca ordini PT” per caricare i risultati.
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import SidebarComponent from '../SidebarComponent.vue';

const DEFAULT_TERMS = [
  'pt',
  'sedute pt',
  'seduta pt',
  'personal trainer',
  '50 pt',
  '50 sedute pt',
  '10 pt',
  '10 sedute pt',
  '20 sedute pt online',
].join('\n');

const createDefaultFilters = () => ({
  startDate: '',
  endDate: '2025-10-31',
  matchMode: 'smart',
  category: '__pt__',
  status: '',
  source: '',
  studio: '',
  customer: '',
  productTerms: DEFAULT_TERMS,
  maxDocs: 3000,
});

const PT_CATEGORY_PRESETS = new Set([
  'pt',
  'servizi_pt',
  'pacchetti_pt',
  'pt_pack',
  'sedute_pt',
  'pt_studio',
  'studio_pt',
]);

const PT_QTY_FIELDS = [
  'pt_quantity',
  'ptQty',
  'ptqty',
  'ptSessions',
  'pt_sessions',
  'bonus_pt',
];

const DEFAULT_STUDIO_OPTIONS = [
  'Tivoli Quartier generale',
  'FF Genova',
  'FF Bologna',
  'FF Bari',
  'FF Pescara',
  'FF Firenze',
  'FF Torino',
  'FF Napoli',
  'FF Milano',
  'FF Milano Città Studi',
  'FF Milano Sempione',
  'FF Roma',
  'FF Roma Eur',
  'FF Salerno',
  'FF Cagliari',
  'FF Padova',
];

const pad2 = (value) => String(value).padStart(2, '0');
const formatInputDate = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

export default {
  name: 'PtOrdersExport',
  components: { SidebarComponent },
  data() {
    return {
      db: null,
      loading: false,
      hasSearched: false,
      error: '',
      limitedByMaxDocs: false,
      lastUpdatedAt: null,
      orders: [],
      products: [],
      userProfilesByUid: {},
      userProfilesByEmail: {},
      ptSessionStatsByUid: {},
      productsLoaded: false,
      productIndex: {
        byId: Object.create(null),
        bySku: Object.create(null),
        byName: Object.create(null),
      },
      filters: createDefaultFilters(),
      statusOptions: [
        'pagato',
        'rateizzato',
        'in attesa di pagamento',
        'scaduto',
        'rimborsato',
        'rimborsato parzialmente',
        'cancellato',
        'wc-completed',
        'wc-processing',
      ],
    };
  },
  computed: {
    categoryOptions() {
      const set = new Set();
      this.products.forEach((product) => {
        const category = (product?.category || product?.product_category || product?.type || '').toString().trim();
        if (category) set.add(category);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
    },
    studioOptions() {
      const studiosByKey = new Map();
      const addStudio = (value) => {
        const studio = String(value || '').trim();
        const key = this.normalizeText(studio);
        if (key && !studiosByKey.has(key)) studiosByKey.set(key, studio);
      };

      DEFAULT_STUDIO_OPTIONS.forEach(addStudio);
      Object.values(this.userProfilesByUid).forEach(profile => addStudio(profile?.studio));
      Object.values(this.userProfilesByEmail).forEach(profile => addStudio(profile?.studio));

      return Array.from(studiosByKey.values())
        .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
    },
    filteredOrders() {
      return this.orders.filter(order => this.orderMatchesBaseFilters(order));
    },
    ptRows() {
      const rows = [];
      this.filteredOrders.forEach((order) => {
        this.buildPtRowsForOrder(order).forEach(row => rows.push(row));
      });
      return rows.sort((a, b) => b.orderTime - a.orderTime);
    },
    uniqueOrderCount() {
      return new Set(this.ptRows.map(row => row.orderId || row.orderDocId)).size;
    },
    totalPtSessions() {
      return this.ptRows.reduce((sum, row) => sum + (Number(row.ptSessions) || 0), 0);
    },
    totalPtDoneSessions() {
      return this.groupedPtRows.reduce((sum, row) => sum + (Number(row.ptDoneSessions) || 0), 0);
    },
    totalPtAmount() {
      return this.ptRows.reduce((sum, row) => sum + (Number(row.productTotal) || 0), 0);
    },
    groupedPtRows() {
      return this.buildGroupedRows(this.ptRows);
    },
  },
  created() {
    this.db = getFirestore();
  },
  methods: {
    resetFilters() {
      this.filters = createDefaultFilters();
    },
    applyBeforeNovember2025() {
      this.filters.startDate = '';
      this.filters.endDate = '2025-10-31';
    },
    applyFromNovember2025() {
      this.filters.startDate = '2025-11-01';
      this.filters.endDate = formatInputDate(new Date());
    },
    clearDates() {
      this.filters.startDate = '';
      this.filters.endDate = '';
    },
    async loadProducts() {
      if (this.productsLoaded) return;
      const snap = await getDocs(query(collection(this.db, 'products'), orderBy('name')));
      this.products = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      this.buildProductIndex();
      this.productsLoaded = true;
    },
    buildProductIndex() {
      const byId = Object.create(null);
      const bySku = Object.create(null);
      const byName = Object.create(null);
      this.products.forEach((product) => {
        const id = this.normalizeCompact(product?.id);
        const sku = this.normalizeCompact(product?.sku);
        const name = this.normalizeText(product?.name);
        if (id) byId[id] = product;
        if (sku) bySku[sku] = product;
        if (name) byName[name] = product;
      });
      this.productIndex = { byId, bySku, byName };
    },
    async searchOrders() {
      if (!this.db || this.loading) return;
      this.loading = true;
      this.hasSearched = true;
      this.error = '';
      this.limitedByMaxDocs = false;

      try {
        try {
          await this.loadProducts();
        } catch (err) {
          console.warn('PtOrdersExport: products non caricati', err);
        }

        const docs = [];
        const baseRef = collection(this.db, 'orders');
        const maxDocs = this.resolveMaxDocs();
        const hasRange = Boolean(this.filters.startDate || this.filters.endDate);
        const fields = hasRange
          ? ['orderDate', 'order_date', 'createdAt', 'creationDate', 'importedAt', 'updatedAt']
          : ['orderDate'];

        for (const field of fields) {
          for (const constraints of this.buildDateConstraintVariants(field, maxDocs)) {
            try {
              const snap = await getDocs(query(baseRef, ...constraints));
              docs.push(...snap.docs);
              if (snap.size >= maxDocs) this.limitedByMaxDocs = true;
            } catch (err) {
              console.warn(`PtOrdersExport: query ${field} fallita`, err);
            }
          }
        }

        if (!docs.length && !hasRange) {
          const snap = await getDocs(query(baseRef, orderBy('orderDate', 'desc'), limit(maxDocs)));
          docs.push(...snap.docs);
          if (snap.size >= maxDocs) this.limitedByMaxDocs = true;
        }

        const seen = new Set();
        this.orders = docs
          .filter((docSnap) => {
            if (!docSnap?.id || seen.has(docSnap.id)) return false;
            seen.add(docSnap.id);
            return true;
          })
          .map(docSnap => this.mapOrderDoc(docSnap))
          .filter(order => this.orderMatchesDate(order));

        await this.ensureUserProfilesForOrders(this.orders);
        await this.ensurePtSessionStatsForOrders(this.orders);
        this.lastUpdatedAt = new Date();
      } catch (err) {
        console.error('PtOrdersExport: errore caricamento ordini', err);
        this.error = 'Errore durante il caricamento degli ordini.';
      } finally {
        this.loading = false;
      }
    },
    resolveMaxDocs() {
      const max = Number(this.filters.maxDocs);
      if (!Number.isFinite(max) || max < 100) return 3000;
      return Math.min(Math.round(max), 20000);
    },
    buildDateConstraintVariants(field, maxDocs) {
      const { startDate, endDate } = this.filters;
      if (!startDate && !endDate) {
        return [[orderBy(field, 'desc'), limit(maxDocs)]];
      }

      const variants = [];
      const start = startDate ? this.dateFromInput(startDate, 'start') : null;
      const end = endDate ? this.dateFromInput(endDate, 'end') : null;

      const timestampConstraints = [];
      if (start) timestampConstraints.push(where(field, '>=', start));
      if (end) timestampConstraints.push(where(field, '<=', end));
      timestampConstraints.push(orderBy(field, 'desc'), limit(maxDocs));
      variants.push(timestampConstraints);

      const stringConstraints = [];
      if (startDate) stringConstraints.push(where(field, '>=', startDate));
      if (endDate) stringConstraints.push(where(field, '<=', `${endDate}\uf8ff`));
      stringConstraints.push(orderBy(field, 'desc'), limit(maxDocs));
      variants.push(stringConstraints);

      return variants;
    },
    dateFromInput(value, edge = 'start') {
      if (!value) return null;
      const [year, month, day] = String(value).split('-').map(Number);
      if (!year || !month || !day) return null;
      return edge === 'end'
        ? new Date(year, month - 1, day, 23, 59, 59, 999)
        : new Date(year, month - 1, day, 0, 0, 0, 0);
    },
    mapOrderDoc(docSnap) {
      const data = docSnap.data() || {};
      const source = data.source
        || (docSnap.id.startsWith('legacy_')
          ? 'legacy'
          : docSnap.id.startsWith('manual_')
            ? 'manuale'
            : docSnap.id.startsWith('export_')
              ? 'woocommerce'
              : '');
      return { _id: docSnap.id, _source: source || this.normalizeSource(data), ...data };
    },
    orderMatchesDate(order) {
      const date = this.parseOrderDate(order);
      if (!date) return true;
      const { startDate, endDate } = this.filters;
      const start = this.dateFromInput(startDate, 'start');
      const end = this.dateFromInput(endDate, 'end');
      if (start && date < start) return false;
      if (end && date > end) return false;
      return true;
    },
    orderMatchesBaseFilters(order) {
      if (this.filters.source && this.normalizeSource(order) !== this.filters.source) return false;
      if (this.filters.status && this.normalizeText(order.status || order.orderStatus) !== this.normalizeText(this.filters.status)) {
        return false;
      }
      if (this.filters.studio) {
        const studio = this.resolveCustomer(order).studio;
        if (this.normalizeText(studio) !== this.normalizeText(this.filters.studio)) return false;
      }
      const customerNeedle = this.normalizeText(this.filters.customer);
      if (customerNeedle) {
        const profile = this.cachedUserProfileForOrder(order) || {};
        const haystack = this.normalizeText([
          order._id,
          order.orderId,
          order.orderNumber,
          order.number,
          order.userEmail,
          order.email,
          order.customer_email,
          order.customerName,
          order.customerSurname,
          order.customer?.email,
          order.customer?.name,
          order.customer?.surname,
          order.billing?.email,
          order.billing?.first_name,
          order.billing?.last_name,
          order.billing?.phone,
          order.phone,
          profile.email,
          profile.name,
          profile.surname,
          profile.phone,
        ].filter(Boolean).join(' '));
        if (!haystack.includes(customerNeedle)) return false;
      }
      return true;
    },
    buildPtRowsForOrder(order) {
      const products = this.safeProducts(order);
      const orderDate = this.parseOrderDate(order);
      const customer = this.resolveCustomer(order);

      return products
        .map((line, index) => {
          const info = this.buildLineInfo(line, order);
          const match = this.resolveLineMatch(info);
          if (!match.matched) return null;

          const ptSessions = this.resolvePtSessions(info);
          const productTotal = this.resolveLineAmount(info.safeLine);
          const orderId = order.orderId || order._id || order.id || '';
          const productId = info.productId || '';
          const productSku = info.sku || '';
          const sessionMode = this.resolveSessionMode(info);
          const uid = this.getResolvedUserUid(order);
          const ptStats = this.resolvePtSessionStatsForLine(order, info, {
            orderId,
            productId,
            productSku,
            productName: info.productName,
            sessionMode,
            estimatedSessions: ptSessions,
            uid,
          });

          return {
            rowKey: `${order._id || orderId}-${index}-${productId || productSku || info.productName}`,
            orderDocId: order._id || '',
            userUid: uid,
            orderId,
            orderNumber: order.orderNumber || order.number || '',
            orderDate: orderDate ? this.formatCsvDate(orderDate) : '',
            orderDateLabel: orderDate ? this.formatDateTime(orderDate) : ' - ',
            orderTime: orderDate ? orderDate.getTime() : 0,
            importedAt: this.formatMaybeDate(order.importedAt),
            status: order.status || order.orderStatus || '',
            paymentStatus: order.paymentStatus || order.payment_status || '',
            paymentMethod: order.paymentMethod || order.payment_method || '',
            paymentProvider: order.paymentProvider || order.payment_provider || '',
            source: this.normalizeSource(order),
            studio: customer.studio,
            customerEmail: customer.email,
            customerFirstName: customer.name,
            customerLastName: customer.surname,
            customerName: [customer.name, customer.surname].filter(Boolean).join(' ').trim(),
            customerPhone: customer.phone,
            totalAmount: this.resolveOrderAmount(order),
            productId,
            productSku,
            productName: info.productName,
            productCategoryKey: info.categoryKey,
            categoryLabel: info.categoryLabel,
            productQuantity: info.quantity,
            ptSessions,
            sessionMode,
            ptSessionTotal: ptStats.sessionTotal,
            ptDoneSessions: ptStats.done,
            ptRemainingSessions: ptStats.remaining,
            ptDoneSource: ptStats.source,
            ptDoneScope: ptStats.scope,
            ptDoneMatched: ptStats.matched,
            sedutePtDocIds: ptStats.docIds.join('|'),
            userPtTotalSessions: ptStats.userTotal,
            userPtDoneSessions: ptStats.userDone,
            userPtRemainingSessions: ptStats.userRemaining,
            productPrice: this.resolveLineUnitPrice(info.safeLine),
            productTotal,
            matchReason: match.reason,
          };
        })
        .filter(Boolean);
    },
    buildLineInfo(line, order) {
      const catalogProduct = this.matchCatalogProduct(line) || {};
      const productName = this.resolveLineDisplayName(line, catalogProduct);
      const safeLine = { ...line, name: productName };
      const categoryKey = this.resolveProductCategory(safeLine, catalogProduct, order);
      return {
        line,
        safeLine,
        catalogProduct,
        productName,
        productId: line.productId || line.product_id || line.id || catalogProduct.id || '',
        sku: line.sku || line.SKU || line.productSku || catalogProduct.sku || '',
        quantity: this.resolveLineQuantity(safeLine),
        categoryKey,
        categoryLabel: this.categoryLabel(categoryKey, safeLine, catalogProduct),
      };
    },
    resolveLineMatch(info) {
      const categoryMatched = this.matchesCategory(info);
      const nameMatched = this.matchesNameSearch(info);
      const mode = this.filters.matchMode;

      if (mode === 'category') {
        return { matched: categoryMatched, reason: categoryMatched ? 'categoria' : '' };
      }
      if (mode === 'name') {
        return { matched: nameMatched, reason: nameMatched ? 'nome' : '' };
      }
      if (categoryMatched && nameMatched) return { matched: true, reason: 'categoria + nome' };
      if (categoryMatched) return { matched: true, reason: 'categoria' };
      if (nameMatched) return { matched: true, reason: 'nome' };
      return { matched: false, reason: '' };
    },
    matchesCategory(info) {
      const selected = this.filters.category;
      if (selected === '__pt__') return info.categoryKey === 'pt';

      const selectedText = this.normalizeText(selected);
      if (!selectedText) return false;

      const candidates = [
        info.line?.category,
        info.line?.productCategory,
        info.line?.type,
        info.line?.group,
        info.catalogProduct?.category,
        info.catalogProduct?.product_category,
        info.catalogProduct?.type,
      ];
      return candidates.some(candidate => this.normalizeText(candidate) === selectedText);
    },
    matchesNameSearch(info) {
      const terms = this.productSearchTerms();
      if (!terms.length) return false;
      const haystack = this.normalizeText([
        info.productName,
        info.sku,
        info.productId,
        info.catalogProduct?.name,
        info.catalogProduct?.sku,
        info.line?.woo,
      ].filter(Boolean).join(' '));
      return terms.some(term => this.termMatchesText(term, haystack));
    },
    productSearchTerms() {
      return String(this.filters.productTerms || '')
        .split(/[\n,;]+/)
        .map(term => this.normalizeText(term))
        .filter(Boolean);
    },
    termMatchesText(term, haystack) {
      if (!term || !haystack) return false;
      if (term.length <= 2) {
        return new RegExp(`\\b${this.escapeRegExp(term)}\\b`, 'i').test(haystack);
      }
      return haystack.includes(term);
    },
    resolveProductCategory(line, catalogProduct) {
      if (this.hasPtQuantity(line) || this.hasPtQuantity(catalogProduct)) return 'pt';

      const direct = line.category || line.productCategory || line.type || line.group;
      if (this.isPtCategoryKey(direct)) return 'pt';

      const catalogCategory = catalogProduct?.category || catalogProduct?.product_category || catalogProduct?.type;
      if (this.isPtCategoryKey(catalogCategory)) return 'pt';

      if (this.isPtProductName(line.name) || this.isPtProductName(catalogProduct?.name)) return 'pt';
      return this.normalizeText(direct || catalogCategory) || 'coaching';
    },
    categoryLabel(categoryKey, line, catalogProduct) {
      if (categoryKey === 'pt') return 'Servizi PT';
      const direct = line?.category || line?.productCategory || catalogProduct?.category || categoryKey;
      return this.formatLabel(direct || 'Coaching');
    },
    hasPtQuantity(entity) {
      if (!entity || typeof entity !== 'object') return false;
      return PT_QTY_FIELDS.some((field) => {
        const value = Number(entity[field]);
        return Number.isFinite(value) && value > 0;
      });
    },
    isPtCategoryKey(value) {
      const key = this.normalizeCompact(value);
      if (!key) return false;
      if (PT_CATEGORY_PRESETS.has(key)) return true;
      if (key.includes('servizi') && key.includes('pt')) return true;
      if (key.includes('sedute') && key.includes('pt')) return true;
      if (key.includes('personal') && key.includes('trainer')) return true;
      if (key.includes('studio') && key.includes('pt')) return true;
      return false;
    },
    isPtProductName(value) {
      const text = this.normalizeText(value);
      if (!text) return false;
      if (this.isPtCategoryKey(text)) return true;
      if (text.includes('sedute pt') || text.includes('seduta pt') || text.includes('pt sedute')) return true;
      if (/\bpt\b/.test(text)) return true;
      return /\b(\d+\s*(sedut[ae]\s*)?pt|pt\s*\d+|pack\s*pt|pacchetti?\s*pt|servizi?\s*pt|sedut[ae]\s*pt)\b/.test(text);
    },
    resolvePtSessions(info) {
      const qty = Number(info.quantity) || 1;
      const explicit = this.firstPositiveNumber([
        ...PT_QTY_FIELDS.map(field => info.line?.[field]),
        ...PT_QTY_FIELDS.map(field => info.catalogProduct?.[field]),
      ]);
      if (explicit) return explicit * qty;

      const inferred = this.inferPtSessionsFromName(info.productName);
      if (inferred) return inferred * qty;

      return qty;
    },
    inferPtSessionsFromName(value) {
      const text = this.normalizeText(value);
      if (!text) return 0;
      const patterns = [
        /\b(\d{1,3})\s*sedut[ae]\s*pt\b/,
        /\b(\d{1,3})\s*pt\b/,
        /\bpt\s*(\d{1,3})\b/,
      ];
      for (const pattern of patterns) {
        const match = text.match(pattern);
        const amount = Number(match?.[1]);
        if (Number.isFinite(amount) && amount > 0) return amount;
      }
      return 0;
    },
    resolveSessionMode(info) {
      const text = this.normalizeText([
        info.productName,
        info.sku,
        info.catalogProduct?.name,
        info.catalogProduct?.sku,
      ].filter(Boolean).join(' '));
      if (text.includes('online') || text.includes('custom')) return 'online';
      return 'studio';
    },
    safeProducts(order) {
      const candidates = [order?.products, order?.line_items, order?.lineItems, order?.items];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) return candidate;
      }
      return [];
    },
    matchCatalogProduct(line) {
      const idCandidates = [line.productId, line.product_id, line.id, line.itemId, line.catalogId];
      for (const candidate of idCandidates) {
        const key = this.normalizeCompact(candidate);
        if (key && this.productIndex.byId[key]) return this.productIndex.byId[key];
      }

      const skuCandidates = [line.sku, line.SKU, line.productSku];
      for (const candidate of skuCandidates) {
        const key = this.normalizeCompact(candidate);
        if (key && this.productIndex.bySku[key]) return this.productIndex.bySku[key];
      }

      const name = this.normalizeText(this.resolveLineNameCandidate(line));
      if (name && this.productIndex.byName[name]) return this.productIndex.byName[name];
      return null;
    },
    resolveLineNameCandidate(line) {
      const candidates = [
        line?.name,
        line?.title,
        line?.productName,
        line?.product_name,
        line?.product_title,
        line?.item_name,
        line?.itemName,
      ];
      return this.firstNonEmpty(candidates);
    },
    resolveLineDisplayName(line, catalogProduct) {
      return this.resolveLineNameCandidate(line) || catalogProduct?.name || 'Senza nome';
    },
    resolveLineQuantity(line) {
      const fields = ['quantity', 'qty', 'amount_qty', 'line_qty', 'count'];
      for (const field of fields) {
        const value = Number(line?.[field]);
        if (Number.isFinite(value) && value !== 0) return Math.abs(value);
      }
      return 1;
    },
    resolveLineUnitPrice(line) {
      const fields = ['price', 'unitPrice', 'unit_price', 'line_price', 'amount'];
      for (const field of fields) {
        const value = Number(line?.[field]);
        if (Number.isFinite(value)) return value;
      }
      return 0;
    },
    resolveLineAmount(line) {
      const fields = ['total', 'line_total', 'subtotal', 'amount', 'line_subtotal', 'row_total', 'total_price'];
      for (const field of fields) {
        const value = Number(line?.[field]);
        if (Number.isFinite(value) && value !== 0) return value;
      }
      return this.resolveLineUnitPrice(line) * this.resolveLineQuantity(line);
    },
    resolveOrderAmount(order) {
      const fields = ['totalPrice', 'total', 'amount', 'grandTotal', 'orderTotal'];
      for (const field of fields) {
        const value = Number(order?.[field]);
        if (Number.isFinite(value)) return value;
      }
      return this.safeProducts(order).reduce((sum, line) => sum + this.resolveLineAmount(line), 0);
    },
    async ensureUserProfilesForOrders(orders = []) {
      const jobs = [];
      const seenUid = new Set();
      const seenEmail = new Set();

      for (const order of orders) {
        if (!this.orderHasPtLine(order)) continue;

        const uid = this.getOrderUserUid(order);
        if (uid && !seenUid.has(uid) && !Object.prototype.hasOwnProperty.call(this.userProfilesByUid, uid)) {
          seenUid.add(uid);
          jobs.push(() => this.fetchUserProfileByUid(uid));
        }

        const rawEmail = this.extractOrderEmail(order);
        const email = this.normalizeEmail(rawEmail);
        if (email && !seenEmail.has(email) && !Object.prototype.hasOwnProperty.call(this.userProfilesByEmail, email)) {
          seenEmail.add(email);
          jobs.push(() => this.fetchUserProfileByEmail(rawEmail));
        }
      }

      await this.runLimited(jobs, 8);
    },
    async ensurePtSessionStatsForOrders(orders = []) {
      const jobs = [];
      const seenUid = new Set();

      for (const order of orders) {
        if (!this.orderHasPtLine(order)) continue;

        const uid = this.getResolvedUserUid(order);
        if (!uid || seenUid.has(uid) || Object.prototype.hasOwnProperty.call(this.ptSessionStatsByUid, uid)) {
          continue;
        }
        seenUid.add(uid);
        jobs.push(() => this.fetchPtSessionStatsByUid(uid));
      }

      await this.runLimited(jobs, 8);
    },
    orderHasPtLine(order) {
      return this.safeProducts(order).some((line) => {
        const info = this.buildLineInfo(line, order);
        return this.resolveLineMatch(info).matched;
      });
    },
    async runLimited(jobs, limitCount = 8) {
      const queue = [...jobs];
      const workers = Array.from({ length: Math.min(limitCount, queue.length) }, async () => {
        while (queue.length) {
          const job = queue.shift();
          try {
            await job();
          } catch (err) {
            console.warn('PtOrdersExport: lookup profilo utente fallito', err);
          }
        }
      });
      await Promise.all(workers);
    },
    async fetchUserProfileByUid(uid) {
      const key = String(uid || '').trim();
      if (!key) return null;
      if (Object.prototype.hasOwnProperty.call(this.userProfilesByUid, key)) {
        return this.userProfilesByUid[key];
      }

      try {
        const snap = await getDoc(doc(this.db, 'users', key));
        const profile = snap.exists() ? this.normalizeUserProfile(snap.id, snap.data() || {}) : null;
        this.userProfilesByUid[key] = profile;
        if (profile?.email) this.userProfilesByEmail[this.normalizeEmail(profile.email)] = profile;
        return profile;
      } catch (err) {
        console.warn(`PtOrdersExport: impossibile leggere users/${key}`, err);
        this.userProfilesByUid[key] = null;
        return null;
      }
    },
    async fetchPtSessionStatsByUid(uid) {
      const key = String(uid || '').trim();
      if (!key) return null;
      if (Object.prototype.hasOwnProperty.call(this.ptSessionStatsByUid, key)) {
        return this.ptSessionStatsByUid[key];
      }

      try {
        const snap = await getDocs(query(collection(this.db, 'sedutept'), where('uid', '==', key)));
        const stats = this.buildPtSessionStats(
          key,
          snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
        );
        this.ptSessionStatsByUid[key] = stats;
        return stats;
      } catch (err) {
        console.warn(`PtOrdersExport: impossibile leggere sedutept per ${key}`, err);
        this.ptSessionStatsByUid[key] = this.emptyPtSessionStats(key);
        return this.ptSessionStatsByUid[key];
      }
    },
    emptyPtSessionStats(uid = '') {
      return {
        uid,
        total: 0,
        done: 0,
        remaining: 0,
        docsCount: 0,
        byOrderRefProduct: new Map(),
        byOrderRef: new Map(),
        byProductMode: new Map(),
        byProductName: new Map(),
      };
    },
    buildPtSessionStats(uid, docs = []) {
      const stats = this.emptyPtSessionStats(uid);

      docs.forEach((docItem) => {
        const slots = Array.isArray(docItem.slots) ? docItem.slots : [];
        const totalFromField = Number(docItem.numeroSedute);
        const total = Number.isFinite(totalFromField) && totalFromField > 0 ? totalFromField : slots.length;
        const done = slots.filter(slot => this.isPtSlotDone(slot)).length;
        const mode = this.resolveSedutaSessionMode(docItem);
        const orderRef = this.firstNonEmpty([docItem.orderRef, docItem.orderId, docItem.order_id]);
        const productId = this.firstNonEmpty([docItem.orderProductId, docItem.productId, docItem.product_id]);
        const productName = this.firstNonEmpty([docItem.productName, docItem.name, docItem.categoriaSeduta, 'Sedute PT']);
        const entry = {
          id: docItem.id,
          orderRef,
          productId,
          productName,
          mode,
          total,
          done,
          remaining: Math.max(total - done, 0),
        };

        stats.total += total;
        stats.done += done;
        stats.docsCount += 1;

        if (orderRef && productId) {
          this.addPtStatsEntry(stats.byOrderRefProduct, `${this.normalizeCompact(orderRef)}|${this.normalizeCompact(productId)}`, entry);
        }
        if (orderRef) {
          this.addPtStatsEntry(stats.byOrderRef, this.normalizeCompact(orderRef), entry);
        }
        if (productName) {
          const productKey = this.normalizeText(productName);
          this.addPtStatsEntry(stats.byProductName, productKey, entry);
          this.addPtStatsEntry(stats.byProductMode, `${productKey}|${this.normalizeText(mode)}`, entry);
        }
      });

      stats.remaining = Math.max(stats.total - stats.done, 0);
      return stats;
    },
    addPtStatsEntry(map, key, entry) {
      if (!key) return;
      const current = map.get(key) || {
        total: 0,
        done: 0,
        remaining: 0,
        docIds: [],
        entries: [],
      };
      current.total += Number(entry.total) || 0;
      current.done += Number(entry.done) || 0;
      current.remaining = Math.max(current.total - current.done, 0);
      if (entry.id) current.docIds.push(entry.id);
      current.entries.push(entry);
      map.set(key, current);
    },
    isPtSlotDone(slot) {
      if (!slot) return false;
      const status = this.normalizeText(slot.status);
      if (status === 'fatta' || status === 'fatto') return true;
      return Boolean(slot.dataEffettuata);
    },
    resolveSedutaSessionMode(docItem = {}) {
      const direct = this.normalizeText(docItem.categoriaSeduta || docItem.sessionMode || docItem.mode);
      if (direct.includes('online')) return 'online';
      if (direct.includes('studio') || direct.includes('instudio')) return 'studio';

      const slots = Array.isArray(docItem.slots) ? docItem.slots : [];
      const come = this.normalizeText(slots.find(slot => slot?.come)?.come);
      if (come.includes('online')) return 'online';
      if (come.includes('studio') || come.includes('instudio')) return 'studio';
      return '';
    },
    resolvePtSessionStatsForLine(order, info, meta = {}) {
      const uid = meta.uid || this.getResolvedUserUid(order);
      const stats = uid ? this.ptSessionStatsByUid[uid] : null;
      const empty = {
        sessionTotal: '',
        done: '',
        remaining: '',
        source: stats?.docsCount ? 'totale cliente' : 'nessun dato sedutept',
        scope: stats?.docsCount ? 'user' : 'none',
        matched: false,
        docIds: [],
        userTotal: stats?.total || 0,
        userDone: stats?.done || 0,
        userRemaining: stats?.remaining || 0,
      };
      if (!stats) return empty;

      const refs = this.orderReferenceCandidates(order, meta.orderId);
      const products = [
        meta.productId,
        info.productId,
        meta.productSku,
        info.sku,
      ].map(value => this.normalizeCompact(value)).filter(Boolean);

      for (const ref of refs) {
        for (const product of products) {
          const exact = stats.byOrderRefProduct.get(`${ref}|${product}`);
          if (exact) return this.formatPtStatsMatch(exact, stats, 'ordine/prodotto', 'line');
        }
      }

      for (const ref of refs) {
        const orderMatch = stats.byOrderRef.get(ref);
        if (orderMatch) return this.formatPtStatsMatch(orderMatch, stats, 'ordine', 'order');
      }

      const productName = this.normalizeText(meta.productName || info.productName);
      const modeKey = `${productName}|${this.normalizeText(meta.sessionMode)}`;
      const productModeMatch = stats.byProductMode.get(modeKey);
      if (productModeMatch) return this.formatPtStatsMatch(productModeMatch, stats, 'prodotto cliente', 'product');

      const productMatch = stats.byProductName.get(productName);
      if (productMatch) return this.formatPtStatsMatch(productMatch, stats, 'prodotto cliente', 'product');

      return empty;
    },
    formatPtStatsMatch(match, userStats, source, scope) {
      return {
        sessionTotal: match.total,
        done: match.done,
        remaining: match.remaining,
        source,
        scope,
        matched: scope === 'line' || scope === 'order',
        docIds: match.docIds || [],
        userTotal: userStats.total || 0,
        userDone: userStats.done || 0,
        userRemaining: userStats.remaining || 0,
      };
    },
    orderReferenceCandidates(order = {}, preferred = '') {
      return [
        preferred,
        order.orderId,
        order._id,
        order.id,
        order.orderNumber,
        order.number,
        order.orderRef,
      ].map(value => this.normalizeCompact(value)).filter(Boolean);
    },
    async fetchUserProfileByEmail(email) {
      const normalized = this.normalizeEmail(email);
      if (!normalized) return null;
      if (Object.prototype.hasOwnProperty.call(this.userProfilesByEmail, normalized)) {
        return this.userProfilesByEmail[normalized];
      }

      const attempts = [];
      const addAttempt = (field, value) => {
        if (!field || !value) return;
        if (attempts.some(item => item.field === field && item.value === value)) return;
        attempts.push({ field, value });
      };

      addAttempt('emailLower', normalized);
      addAttempt('email', email);
      addAttempt('email', normalized);

      for (const attempt of attempts) {
        try {
          const snap = await getDocs(
            query(collection(this.db, 'users'), where(attempt.field, '==', attempt.value), limit(1))
          );
          if (!snap.empty) {
            const docSnap = snap.docs[0];
            const profile = this.normalizeUserProfile(docSnap.id, docSnap.data() || {});
            this.userProfilesByEmail[normalized] = profile;
            if (profile.uid) this.userProfilesByUid[profile.uid] = profile;
            return profile;
          }
        } catch (err) {
          console.warn(`PtOrdersExport: query users ${attempt.field} fallita`, err);
        }
      }

      this.userProfilesByEmail[normalized] = null;
      return null;
    },
    normalizeUserProfile(id, data = {}) {
      const displayName = this.firstNonEmpty([
        data.displayName,
        data.fullName,
        data.full_name,
        data.nomeCompleto,
      ]);
      const split = this.splitFullName(displayName);
      const email = this.firstNonEmpty([data.email, data.emailLower, data.userEmail]);
      return {
        uid: this.firstNonEmpty([id, data.uid, data.userUid]),
        email,
        name: this.firstNonEmpty([data.name, data.firstName, data.first_name, data.nome, split.name]),
        surname: this.firstNonEmpty([data.surname, data.surame, data.lastName, data.last_name, data.cognome, split.surname]),
        phone: this.firstNonEmpty([data.phone, data.telefono, data.userPhone]),
        studio: this.firstNonEmpty([data.studio, data.studioName, data.parent_id]),
      };
    },
    splitFullName(value) {
      const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return { name: '', surname: '' };
      if (parts.length === 1) return { name: parts[0], surname: '' };
      return { name: parts.shift(), surname: parts.join(' ') };
    },
    cachedUserProfileForOrder(order) {
      const uid = this.getOrderUserUid(order);
      if (uid && Object.prototype.hasOwnProperty.call(this.userProfilesByUid, uid)) {
        return this.userProfilesByUid[uid];
      }
      const email = this.normalizeEmail(this.extractOrderEmail(order));
      if (email && Object.prototype.hasOwnProperty.call(this.userProfilesByEmail, email)) {
        return this.userProfilesByEmail[email];
      }
      return null;
    },
    getOrderUserUid(order = {}) {
      return this.firstNonEmpty([
        order.userUid,
        order.userId,
        order.uid,
        order.user_uid,
        order.user_id,
        order.customerUid,
        order.customer_id,
        order.customerId,
        order.customer_uid,
        order.user?.uid,
      ]);
    },
    getResolvedUserUid(order = {}) {
      return this.getOrderUserUid(order) || this.cachedUserProfileForOrder(order)?.uid || '';
    },
    extractOrderEmail(order = {}) {
      return this.firstNonEmpty([
        order.userEmail,
        order.email,
        order.customerEmail,
        order.customer_email,
        order.customer?.email,
        order.billing?.email,
        order.shipping?.email,
      ]);
    },
    resolveCustomer(order) {
      const profile = this.cachedUserProfileForOrder(order) || {};
      const email = this.firstNonEmpty([
        profile.email,
        order.userEmail,
        order.email,
        order.customerEmail,
        order.customer_email,
        order.customer?.email,
        order.billing?.email,
        order.shipping?.email,
      ]);
      const orderFullName = this.firstNonEmpty([
        order.customerFullName,
        order.customerName,
        order.customer_name,
        order.customer?.fullName,
        order.customer?.full_name,
      ]);
      const splitOrderName = this.splitFullName(orderFullName);
      const name = this.firstNonEmpty([
        profile.name,
        order.customerFirstName,
        order.first_name,
        order.customer?.firstName,
        order.customer?.first_name,
        order.billing?.first_name,
        order.billing?.firstName,
        order.shipping?.first_name,
        order.shipping?.firstName,
        splitOrderName.name,
      ]);
      const surname = this.firstNonEmpty([
        profile.surname,
        order.customerLastName,
        order.customer_surname,
        order.last_name,
        order.customer?.lastName,
        order.customer?.last_name,
        order.customer?.surname,
        order.billing?.last_name,
        order.billing?.lastName,
        order.shipping?.last_name,
        order.shipping?.lastName,
        splitOrderName.surname,
      ]);
      return {
        email,
        name,
        surname,
        phone: this.firstNonEmpty([profile.phone, order.customerPhone, order.phone, order.userPhone, order.billing?.phone, order.shipping?.phone]),
        studio: this.firstNonEmpty([profile.studio, order.customerStudio, order.userStudio, order.studio, order.customer?.studio, order.billing?.company]),
      };
    },
    parseOrderDate(order) {
      return this.toDate(
        order?.orderDate
          || order?.order_date
          || order?.createdAt
          || order?.creationDate
          || order?.importedAt
          || order?.updatedAt
      );
    },
    toDate(value) {
      if (!value) return null;
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      if (typeof value.toDate === 'function') {
        try {
          return value.toDate();
        } catch {
          return null;
        }
      }
      if (typeof value === 'object' && typeof value.seconds === 'number') {
        return new Date(value.seconds * 1000);
      }
      if (typeof value === 'number') {
        const millis = value < 10000000000 ? value * 1000 : value;
        const date = new Date(millis);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const raw = String(value).trim();
      const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
      }
      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    normalizeSource(order) {
      const explicit = (order.source || order._source || '').toString().trim().toLowerCase();
      if (explicit) return explicit;
      const id = (order._id || order.orderId || '').toString().toLowerCase();
      if (id.startsWith('legacy_')) return 'legacy';
      if (id.startsWith('manual_')) return 'manuale';
      if (id.startsWith('export_')) return 'woocommerce';
      return 'woocommerce';
    },
    normalizeText(value) {
      return (value === null || value === undefined ? '' : String(value))
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    normalizeCompact(value) {
      return this.normalizeText(value).replace(/\s+/g, '_');
    },
    normalizeEmail(value) {
      return String(value || '').trim().toLowerCase();
    },
    firstNonEmpty(values) {
      for (const value of values) {
        if (value !== null && value !== undefined && String(value).trim()) return String(value).trim();
      }
      return '';
    },
    firstPositiveNumber(values) {
      for (const value of values) {
        const number = Number(value);
        if (Number.isFinite(number) && number > 0) return number;
      }
      return 0;
    },
    escapeRegExp(value) {
      return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    formatLabel(value) {
      if (!value) return '-';
      return String(value)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('it-IT');
    },
    formatOptionalNumber(value) {
      if (value === '' || value === null || value === undefined) return '-';
      return this.formatNumber(value);
    },
    ptDoneLabel(row) {
      if (row.ptDoneSessions !== '' && row.ptDoneSource) return row.ptDoneSource;
      if (row.userPtTotalSessions) {
        return `cliente ${this.formatNumber(row.userPtDoneSessions)}/${this.formatNumber(row.userPtTotalSessions)}`;
      }
      return row.ptDoneSource || 'nessun dato';
    },
    formatDateTime(date) {
      return date instanceof Date && !Number.isNaN(date.getTime())
        ? date.toLocaleString('it-IT')
        : '-';
    },
    formatMaybeDate(value) {
      const date = this.toDate(value);
      return date ? this.formatDateTime(date) : '';
    },
    formatCsvDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    buildGroupedRows(rows = []) {
      const grouped = new Map();

      for (const row of rows) {
        const customerKey = this.normalizeEmail(row.customerEmail)
          || this.normalizeText(row.customerName)
          || this.normalizeText(`${row.customerFirstName || ''} ${row.customerLastName || ''}`)
          || this.normalizeText(row.customerPhone)
          || 'senza_cliente';
        const productKey = this.normalizeCompact(row.productSku)
          || this.normalizeCompact(row.productId)
          || this.normalizeText(row.productName)
          || 'senza_prodotto';
        const key = [
          customerKey,
          productKey,
          this.normalizeText(row.productName),
          this.normalizeText(row.sessionMode),
        ].join('|');

        if (!grouped.has(key)) {
          grouped.set(key, {
            customerEmail: row.customerEmail || '',
            customerFirstName: row.customerFirstName || '',
            customerLastName: row.customerLastName || '',
            customerName: row.customerName || '',
            customerPhone: row.customerPhone || '',
            studio: row.studio || '',
            productId: row.productId || '',
            productSku: row.productSku || '',
            productName: row.productName || '',
            productCategory: row.categoryLabel || '',
            productCategoryKey: row.productCategoryKey || '',
            sessionMode: row.sessionMode || '',
            orderCount: 0,
            productQuantity: 0,
            ptSessions: 0,
            ptSessionTotal: '',
            ptDoneSessions: '',
            ptRemainingSessions: '',
            ptDoneSource: '',
            ptDoneScope: '',
            ptDoneMatchedRows: 0,
            sedutePtDocIds: new Set(),
            userPtTotalSessions: row.userPtTotalSessions || 0,
            userPtDoneSessions: row.userPtDoneSessions || 0,
            userPtRemainingSessions: row.userPtRemainingSessions || 0,
            productTotal: 0,
            firstOrderDate: '',
            lastOrderDate: '',
            orderIds: new Set(),
            sources: new Set(),
            statuses: new Set(),
            matchReasons: new Set(),
            firstTime: Infinity,
            lastTime: -Infinity,
          });
        }

        const entry = grouped.get(key);
        entry.orderCount += 1;
        entry.productQuantity += Number(row.productQuantity) || 0;
        entry.ptSessions += Number(row.ptSessions) || 0;
        entry.productTotal += Number(row.productTotal) || 0;

        const doneScope = row.ptDoneScope || 'none';
        const doneNumber = Number(row.ptDoneSessions);
        const totalNumber = Number(row.ptSessionTotal);
        const hasDoneNumber = Number.isFinite(doneNumber);
        const hasTotalNumber = Number.isFinite(totalNumber);
        if (row.ptDoneMatched) {
          entry.ptDoneMatchedRows += 1;
          entry.ptDoneSessions = (Number(entry.ptDoneSessions) || 0) + (hasDoneNumber ? doneNumber : 0);
          entry.ptSessionTotal = (Number(entry.ptSessionTotal) || 0) + (hasTotalNumber ? totalNumber : 0);
          entry.ptRemainingSessions = Math.max((Number(entry.ptSessionTotal) || 0) - (Number(entry.ptDoneSessions) || 0), 0);
          entry.ptDoneScope = doneScope;
        } else if (!entry.ptDoneMatchedRows && hasDoneNumber && doneScope === 'product') {
          entry.ptDoneSessions = Math.max(Number(entry.ptDoneSessions) || 0, doneNumber);
          entry.ptSessionTotal = hasTotalNumber ? Math.max(Number(entry.ptSessionTotal) || 0, totalNumber) : entry.ptSessionTotal;
          entry.ptRemainingSessions = hasTotalNumber ? Math.max(Number(entry.ptSessionTotal) - Number(entry.ptDoneSessions), 0) : '';
          entry.ptDoneScope = doneScope;
        }

        if (row.ptDoneSource) {
          entry.ptDoneSource = entry.ptDoneSource
            ? `${entry.ptDoneSource}|${row.ptDoneSource}`
            : row.ptDoneSource;
        }
        if (row.sedutePtDocIds) {
          String(row.sedutePtDocIds).split('|').filter(Boolean).forEach(id => entry.sedutePtDocIds.add(id));
        }
        if (row.userPtTotalSessions) entry.userPtTotalSessions = row.userPtTotalSessions;
        if (row.userPtDoneSessions) entry.userPtDoneSessions = row.userPtDoneSessions;
        if (row.userPtRemainingSessions) entry.userPtRemainingSessions = row.userPtRemainingSessions;
        if (!entry.customerFirstName && row.customerFirstName) entry.customerFirstName = row.customerFirstName;
        if (!entry.customerLastName && row.customerLastName) entry.customerLastName = row.customerLastName;
        if (!entry.customerName && row.customerName) entry.customerName = row.customerName;
        if (!entry.customerPhone && row.customerPhone) entry.customerPhone = row.customerPhone;
        if (!entry.studio && row.studio) entry.studio = row.studio;

        if (row.orderId) entry.orderIds.add(row.orderId);
        if (row.source) entry.sources.add(row.source);
        if (row.status) entry.statuses.add(row.status);
        if (row.matchReason) entry.matchReasons.add(row.matchReason);

        const time = Number(row.orderTime);
        if (Number.isFinite(time)) {
          if (time < entry.firstTime) {
            entry.firstTime = time;
            entry.firstOrderDate = row.orderDate || '';
          }
          if (time > entry.lastTime) {
            entry.lastTime = time;
            entry.lastOrderDate = row.orderDate || '';
          }
        }
      }

      return Array.from(grouped.values())
        .map((entry) => ({
          ...entry,
          productQuantity: this.roundCsvNumber(entry.productQuantity),
          ptSessions: this.roundCsvNumber(entry.ptSessions),
          ptSessionTotal: entry.ptSessionTotal === '' ? '' : this.roundCsvNumber(entry.ptSessionTotal),
          ptDoneSessions: entry.ptDoneSessions === '' ? '' : this.roundCsvNumber(entry.ptDoneSessions),
          ptRemainingSessions: entry.ptRemainingSessions === '' ? '' : this.roundCsvNumber(entry.ptRemainingSessions),
          ptDoneSource: Array.from(new Set(String(entry.ptDoneSource || '').split('|').filter(Boolean))).join('|'),
          sedutePtDocIds: Array.from(entry.sedutePtDocIds).join('|'),
          userPtTotalSessions: this.roundCsvNumber(entry.userPtTotalSessions),
          userPtDoneSessions: this.roundCsvNumber(entry.userPtDoneSessions),
          userPtRemainingSessions: this.roundCsvNumber(entry.userPtRemainingSessions),
          productTotal: this.roundCsvNumber(entry.productTotal),
          orderIds: Array.from(entry.orderIds).join('|'),
          sources: Array.from(entry.sources).join('|'),
          statuses: Array.from(entry.statuses).join('|'),
          matchReasons: Array.from(entry.matchReasons).join('|'),
          firstTime: undefined,
          lastTime: undefined,
        }))
        .sort((a, b) => {
          const nameCompare = String(a.customerName || a.customerEmail || '').localeCompare(
            String(b.customerName || b.customerEmail || ''),
            'it',
            { sensitivity: 'base' }
          );
          if (nameCompare) return nameCompare;
          return String(a.productName || '').localeCompare(String(b.productName || ''), 'it', { sensitivity: 'base' });
        });
    },
    roundCsvNumber(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return 0;
      return Math.round(number * 100) / 100;
    },
    exportCsv() {
      if (!this.ptRows.length) {
        alert('Nessuna riga PT da esportare.');
        return;
      }

      const headers = [
        'orderId',
        'orderNumber',
        'userUid',
        'orderDate',
        'importedAt',
        'status',
        'paymentStatus',
        'paymentMethod',
        'paymentProvider',
        'source',
        'studio',
        'customerEmail',
        'customerFirstName',
        'customerLastName',
        'customerPhone',
        'totalAmount',
        'productId',
        'productSku',
        'productName',
        'productCategory',
        'productCategoryKey',
        'productQuantity',
        'ptSessions',
        'ptSessionTotal',
        'ptDoneSessions',
        'ptRemainingSessions',
        'ptDoneSource',
        'ptDoneScope',
        'sedutePtDocIds',
        'userPtTotalSessions',
        'userPtDoneSessions',
        'userPtRemainingSessions',
        'sessionMode',
        'productPrice',
        'productTotal',
        'matchReason',
      ];
      const lines = [
        headers.join(';'),
        ...this.ptRows.map(row => headers.map(key => this.escapeCsvValue(row[key])).join(';')),
      ];
      this.downloadCsv(lines.join('\n'), this.csvFilename());
    },
    exportGroupedCsv() {
      const rows = this.groupedPtRows;
      if (!rows.length) {
        alert('Nessuna riga PT da esportare.');
        return;
      }

      const headers = [
        'customerEmail',
        'customerFirstName',
        'customerLastName',
        'customerName',
        'customerPhone',
        'studio',
        'productId',
        'productSku',
        'productName',
        'productCategory',
        'productCategoryKey',
        'sessionMode',
        'orderCount',
        'productQuantity',
        'ptSessions',
        'ptSessionTotal',
        'ptDoneSessions',
        'ptRemainingSessions',
        'ptDoneSource',
        'ptDoneScope',
        'sedutePtDocIds',
        'userPtTotalSessions',
        'userPtDoneSessions',
        'userPtRemainingSessions',
        'productTotal',
        'firstOrderDate',
        'lastOrderDate',
        'orderIds',
        'sources',
        'statuses',
        'matchReasons',
      ];
      const lines = [
        headers.join(';'),
        ...rows.map(row => headers.map(key => this.escapeCsvValue(row[key])).join(';')),
      ];
      this.downloadCsv(lines.join('\n'), this.csvGroupedFilename());
    },
    csvFilename() {
      const start = this.filters.startDate || 'inizio';
      const end = this.filters.endDate || 'oggi';
      return `ordini_pt_${start}_${end}.csv`;
    },
    csvGroupedFilename() {
      const start = this.filters.startDate || 'inizio';
      const end = this.filters.endDate || 'oggi';
      return `ordini_pt_raggruppati_${start}_${end}.csv`;
    },
    downloadCsv(content, filename) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    escapeCsvValue(value) {
      if (value === null || value === undefined) return '';
      const raw = value instanceof Date ? value.toISOString() : String(value);
      const sanitized = raw.replace(/"/g, '""');
      return /[";\n,]/.test(raw) ? `"${sanitized}"` : sanitized;
    },
  },
};
</script>

<style scoped>
.dashboard-layout {
  display: flex;
}

.content-container {
  flex: 1;
  padding: 24px 24px 24px var(--staff-content-inset, 24px);
}

.pt-orders-export {
  display: grid;
  gap: 18px;
}

.subtitle,
.muted,
.filters-note,
.field small,
.results-table small {
  color: #6b7280;
}

.card-surface {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-wide {
  grid-column: span 2;
}

.field-full {
  grid-column: 1 / -1;
}

label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #374151;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font: inherit;
  background: #ffffff;
}

textarea {
  resize: vertical;
}

.quick-actions,
.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.toolbar-actions {
  margin-top: 16px;
}

.btn {
  border: 0;
  border-radius: 8px;
  padding: 10px 14px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.primary {
  background: #111827;
  color: #ffffff;
}

.btn.ghost {
  background: #f3f4f6;
  color: #111827;
}

.btn.tiny {
  padding: 8px 10px;
  font-size: 0.84rem;
}

.btn .material-icons {
  font-size: 18px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.metric-box {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: grid;
  gap: 6px;
}

.metric-label {
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 700;
}

.metric-value {
  color: #111827;
  font-size: 1.45rem;
  font-weight: 800;
}

.table-wrap {
  overflow-x: auto;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1100px;
}

.results-table th,
.results-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: top;
}

.results-table th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0;
  color: #6b7280;
}

.results-table td strong,
.results-table td small {
  display: block;
}

.status-chip,
.match-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  background: #eef2ff;
  color: #312e81;
  white-space: nowrap;
}

.match-pill {
  background: #ecfdf5;
  color: #065f46;
}

.error-card {
  color: #991b1b;
  background: #fef2f2;
  border-color: #fecaca;
}

.empty-state {
  color: #6b7280;
}

@media (max-width: 1100px) {
  .filters-grid,
  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .field-wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 700px) {
  .content-container {
    padding: 14px;
  }

  .filters-grid,
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .field-wide,
  .field-full {
    grid-column: auto;
  }
}
</style>
